import type { GetScopedTypesenseKey } from '@cloudFunctions/types/typesense/getScopedTypesenseKey';
import type { BackendRouteDefinition } from '../types';

// Phase 5 / Sub-phase 7 / S7-WB3 (PRA-800): the two Typesense callables flip
// from the Firebase callable transport to HTTP. Different services because
// the runtime profiles diverge:
//
//   * `getScopedTypesenseKey` — hot path, every search query mints a key —
//     served by api-core-node at `POST /api/firms/typesense/scoped-key`,
//     which falls through the catch-all `/api/**` Hosting rewrite.
//   * `performInitialTypesenseSync` — one-shot full-firm reindex, can run
//     up to 5 minutes — served by api-longrun-node at
//     `POST /api/firms/typesense/sync`, which has its own Hosting rewrite
//     ahead of `/api/**` so it lands on the long-timeout service rather
//     than api-core-node. Cloud Run timeout for the longrun service is
//     pinned at 300 s (`scripts/deployoy/src/apiLongrunNodeConfig.ts`),
//     matching the legacy callable's `timeoutSeconds: 300`.
//
// `path` is the route's leaf — `apiBaseUrl` (see
// `shared/backend/config.ts` → `resolveApiBaseUrl`) contributes the `/api`
// prefix at invoke time. A leading `/api/` here would produce
// `/api/api/firms/typesense/...` and miss the dev proxy / Hosting rewrite.
//
// The legacy callable exports remain in `cloudFunctions/` until the
// S9-WB2 retirement sweep. The Firestore triggers
// (`syncNewHumanToTypesense` and friends) are unrelated to this migration
// and stay in `cloudFunctions/` permanently — they need the Firestore
// trigger runtime, which only Firebase Functions provides.

export const getScopedTypesenseKeyRoute = {
  transport: 'api',
  method: 'POST',
  path: '/firms/typesense/scoped-key',
  body: (requestData: GetScopedTypesenseKey.Props) => requestData,
} as const satisfies BackendRouteDefinition;

export const performInitialTypesenseSyncRoute = {
  transport: 'api',
  method: 'POST',
  path: '/firms/typesense/sync',
  // The legacy callable took no arguments. We send an empty JSON body so
  // Fastify's body parser stays happy; the route's Zod schema folds
  // anything non-object into `{}`.
  body: () => ({}),
} as const satisfies BackendRouteDefinition;
